import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { mergeMap, take } from 'rxjs';

import { SortOrder } from '@efficiently/catalog-models';

import { RoomSpaceRepository } from '@efficiently/client-api-project-rooms';
import { ProjectsRepository } from '@efficiently/client-api-projects';
import { PAGES_ROUTES, PROJECT_DETAIL_ROUTES } from '@efficiently/client-core-routing';
import { ShortUUIDService } from '@efficiently/client-core-utils';
import { ProjectListSelectors } from '@efficiently/client-domain-project-list';

import { SearchParams } from '@ds/shared/modules/item-catalog/item-catalog-list/item-catalog-list.models';
import { ITEM_CATALOG_LIST_ROUTES } from '@ds/shared/modules/item-catalog/item-catalog-list/item-catalog-list-routing.const';
import { SearchItems } from '@ds/store/shared/item-catalog/item-catalog-list/item-catalog-list.actions';

@Injectable({
	providedIn: 'root',
})
export class AiToolsControlService {
	private readonly router = inject(Router);
	private readonly store = inject(Store);
	private readonly shortUUIDService = inject(ShortUUIDService);
	private readonly projectsRepository = inject(ProjectsRepository);
	private readonly roomSpaceRepository = inject(RoomSpaceRepository);
	// TODO EFF-13458 - remove 'any' type
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public async navigation(request: any): Promise<void> {
		if (request.route === 'ProjectList' || (request.route === 'Project' && !request.projectName)) {
			this.router.navigateByUrl(PAGES_ROUTES.PROJECTS.fullPath());
		} else if (request.route === 'Project') {
			const projects = this.store.selectSnapshot(ProjectListSelectors.projects);
			const findProject = projects.find(
				project => project.name?.toLocaleLowerCase() === request.projectName?.toLocaleLowerCase(),
			);
			if (findProject) {
				this.goToProject(findProject._id);
			}
		} else if (request.route === 'Catalog') {
			this.router.navigateByUrl(ITEM_CATALOG_LIST_ROUTES.ITEMS.fullPath());
		}
	}

	public async createProject(projectName: string): Promise<void> {
		this.projectsRepository
			.createProject({ name: projectName })
			.pipe(
				mergeMap(proj => this.roomSpaceRepository.createDefaultRoomAndSpace(proj._id)),
				take(1),
			)
			.subscribe(({ room }) => {
				this.goToProject(room.projectId);
			});
	}

	private goToProject(projectId: string): void {
		const shortenedProjectId = this.shortUUIDService.from(projectId);
		this.router?.navigate([PROJECT_DETAIL_ROUTES.SUMMARY.fullPath(shortenedProjectId)]);
	}

	public async search(query: string): Promise<void> {
		const searchParams = new SearchParams({ isRecent: false, order: SortOrder.Relevant });
		this.store.dispatch(new SearchItems(query, searchParams, { showSpinner: true }));
	}
}
